import React, { useMemo } from "react";
import { Source, Layer } from "react-map-gl/maplibre";
import { useThemeColors } from "../../hooks/useThemeColors";
import type { BaselineCell } from "../../services/exposureApi";

export const LOW_ALPHA = 0.12;
export const HIGH_ALPHA = 0.8;

/** Sequential ramp for the cell layer, from theme tokens: faint brand green up to strong brand green. */
export function useBaselineRamp(): { low: string; high: string } {
  const colors = useThemeColors();
  return useMemo(() => ({ low: colors.css("brand-500", LOW_ALPHA), high: colors.css("brand-700", HIGH_ALPHA) }), [colors]);
}

/** Highest cell value, at least 1 so the ramp always has two distinct stops. */
export const maxCellValue = (cells: BaselineCell[]): number => cells.reduce((m, c) => Math.max(m, c.value), 1);

/**
 * The 250 m cells as a choropleth. Render it inside the map, after the area polygons: it asks to
 * sit beneath the first area fill (`fill-0`), so the area outlines stay on top of it.
 */
const BaselineCellLayer: React.FC<{ cells: BaselineCell[] }> = ({ cells }) => {
  const ramp = useBaselineRamp();
  const data = useMemo(
    () => ({
      type: "FeatureCollection" as const,
      features: cells.map((c) => ({
        type: "Feature" as const,
        geometry: c.geometry,
        properties: { ssbid: c.ssbid, value: c.value },
      })),
    }),
    [cells],
  );
  const max = maxCellValue(cells);
  return (
    <Source id="baseline-cells" type="geojson" data={data as any}>
      <Layer
        id="baseline-cells-fill"
        type="fill"
        beforeId="fill-0"
        paint={{ "fill-color": ["interpolate", ["linear"], ["get", "value"], 0, ramp.low, max, ramp.high] } as any}
      />
    </Source>
  );
};

export default BaselineCellLayer;
